import { Inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';

import { UsersLoggedIn } from '@app/collections/users/users.actions';
import {
  Auth0Client,
  GetTokenSilentlyVerboseResponse,
  AuthorizationParams,
} from '@auth0/auth0-spa-js';
import { REFRESH_TOKEN_DURATION_MS } from '@core/auth/auth.const';
import { MojoAuthorizationService } from '@core/auth/mojo-authorization.service';
import { environment } from '@env/environment';
import { Store } from '@ngrx/store';
import { PrimaryAlert } from '@shared/alert';
import { LocalStorageService } from '@shared/services/local-storage.service';
import { tenantNameFromPath } from '@shared/utils';
import { ReplaySubject, Observable, from } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';

import { TENANT_CONFIG, TenantConfig } from './tenant.config';

export interface Auth0State {
  targetUrl: string;
  nonceRandomValue: number;
  href: string;
}

@Injectable()
export class AuthService {
  // when handling authentication redirect we should stop angular navigation
  // handled via AuthGuard
  preventNavigation = false;

  private readonly storedStateKey = 'auth0_state';
  private readonly beforeLogoutStateKey = 'before_logout_state';

  private auth0State: Auth0State = this.generateAuth0State();

  get mojoJobUiUrl(): string {
    return environment.MOJO_JOB_UI_URL;
  }

  public accessToken = null;
  public expiresAt = null;
  public loginTime: number;

  private auth0 = new Auth0Client({
    domain: environment.AUTH0_DOMAIN,
    clientId: environment.AUTH0_CLIENT_ID,
    cacheLocation: 'memory',
    useRefreshTokens: true,
  });

  private initialized$ = new ReplaySubject(1);

  get secondsLeftInSession(): number {
    return Math.trunc(this.timeLeftInSessionMs / 1000);
  }

  get secondsSinceLogin(): number {
    return Math.trunc((Date.now() - this.loginTime) / 1000);
  }

  get timeLeftInSessionMs(): number {
    if (!this.expiresAt) {
      return 0;
    }
    return this.timeToTokenDeactivationFromNowMs - REFRESH_TOKEN_DURATION_MS;
  }

  get timeToTokenDeactivationFromNowMs(): number {
    return this.expiresAt - Date.now();
  }

  isAuthenticated$ = this.initialized$.pipe(
    map(() => {
      if (!this.accessToken) {
        return false;
      }
      return Date.now() < this.expiresAt;
    }),
    shareReplay(1),
  );

  constructor(
    private localStorage: LocalStorageService,
    public router: Router,
    private store: Store<{}>,
    private mojoAuthorizationService: MojoAuthorizationService,
    @Inject(TENANT_CONFIG) private tenantConfig: TenantConfig,
  ) {}

  handleAuthentication() {
    return new Promise<boolean>((resolve, reject) =>
      this.auth0
        .isAuthenticated()
        .then((authenticated) =>
          authenticated
            ? this.handleAlreadyAuthenticated(resolve, reject)
            : this.handleNotAuthenticated(resolve, reject),
        ),
    );
  }

  private handleNotAuthenticated(
    resolve: (value: PromiseLike<boolean> | boolean) => void,
    reject: (reason?: any) => void,
  ) {
    this.auth0.handleRedirectCallback().then(
      (result) => {
        this.getUser(result.appState);
        resolve(!!result);
      },
      () => {
        this.login();
        reject();
      },
    );
  }

  private handleAlreadyAuthenticated(
    resolve: (value: PromiseLike<boolean> | boolean) => void,
    reject: (reason?: any) => void,
  ) {
    this.auth0
      .getTokenSilently({
        authorizationParams: this.authorizationParams(),
        detailedResponse: true,
      })
      .then((token) => {
        this.setSession(token);
        this.store.dispatch(new UsersLoggedIn());
        resolve(true);
      })
      .catch((error) => {
        console.error('Error getting token:', error);
        this.logout(false);
        reject();
      });
  }

  login() {
    this.preventNavigation = true;
    this.localStorage.setSavedState(this.auth0State, this.storedStateKey);
    return this.auth0.loginWithRedirect({
      appState: JSON.stringify(this.auth0State),
      authorizationParams: {
        redirect_uri: environment.AUTH0_REDIRECT_UI,
        audience: environment.AUTH0_AUDIENCE,
        connection: tenantNameFromPath(environment.DEFAULT_DEV_TENANT),
      },
    });
  }

  logout(notifyAuthService = true) {
    this.preventNavigation = true;
    this.localStorage.setSavedState(this.generateAuth0State(), this.beforeLogoutStateKey);
    if (notifyAuthService) {
      this.mojoAuthorizationService.logout(this.auth0, environment.AUTH0_LOGOUT_URL);
    } else {
      this.mojoAuthorizationService.logoutFromAuth0(this.auth0, environment.AUTH0_LOGOUT_URL);
    }
  }

  private getUser(state: string) {
    return this.auth0.getUser().then((profile) => {
      if (profile) {
        this.store.dispatch(
          new PrimaryAlert(`Welcome to the Fastr.ai platform ${profile.nickname}`),
        );
      }
      location.hash = '';
      this.localStorage.setSavedState(tenantNameFromPath(environment.DEFAULT_DEV_TENANT), 'tenant');

      this.auth0
        .getTokenSilently({
          authorizationParams: this.authorizationParams(),
          detailedResponse: true,
        })
        .then((token) => {
          this.setSession(token);
          this.setLoginTime();
          this.store.dispatch(new UsersLoggedIn());
          this.redirectToTargetUrl(JSON.parse(state));
        });
    });
  }

  private setLoginTime() {
    this.mojoAuthorizationService.getLoginTime().subscribe(
      (loginTime) => (this.loginTime = new Date(loginTime).getTime()),
      (err) => console.error('failed while getting last login time', err),
    );
  }

  private authorizationParams(): AuthorizationParams {
    return { audience: environment.AUTH0_AUDIENCE };
  }

  private setSession(token: GetTokenSilentlyVerboseResponse) {
    const accessTokenChanged = this.accessToken !== token.access_token;

    this.expiresAt = token.expires_in * 1000 + Date.now();
    this.accessToken = token.access_token;

    if (accessTokenChanged) {
      this.mojoAuthorizationService.getBrowserIdCookie();
      this.initialized$.next(undefined);
    }
  }

  public getAccessToken(): Observable<string> {
    return from(
      this.auth0.getTokenSilently({
        authorizationParams: this.authorizationParams(),
        detailedResponse: true,
      }),
    ).pipe(
      map((token) => {
        this.setSession(token);
        return token.access_token;
      }),
    );
  }

  public renewToken(forceRefresh = false): Promise<void> {
    return this.auth0
      .getTokenSilently({
        authorizationParams: this.authorizationParams(),
        detailedResponse: true,
        cacheMode: forceRefresh ? 'off' : 'on',
      })
      .then((token) => this.setSession(token))
      .catch((error) => {
        console.error('Error renewing token:', error);
        this.logout(false);
      });
  }

  private generateAuth0State(): Auth0State {
    const randomValue = Math.floor(Math.random() * 10000);
    return {
      targetUrl: location.hash.replace('#', ''),
      nonceRandomValue: randomValue,
      href: location.href,
    };
  }

  private redirectToTargetUrl(auth0State: Auth0State) {
    const storedAuth0State: any = this.localStorage.getSavedStateWithDefaultValue(
      this.storedStateKey,
      {},
    );
    if (!storedAuth0State.nonceRandomValue) {
      return;
    }

    if (storedAuth0State.nonceRandomValue !== auth0State.nonceRandomValue) {
      this.localStorage.remove(this.storedStateKey);
      this.logout();
    } else {
      const stateBeforeLogout: Auth0State = this.localStorage.getSavedState(
        this.beforeLogoutStateKey,
      );
      if (stateBeforeLogout != null) {
        this.localStorage.remove(this.beforeLogoutStateKey);
        this.redirectToPath(stateBeforeLogout);
        return;
      }

      this.redirectToPath(auth0State);
    }
  }

  private redirectToPath(auth0State: Auth0State) {
    const queryParams = auth0State.targetUrl.match(/\?(.*)/)?.[1];
    const outlets = auth0State.targetUrl.match(/\((.*?)\)/)?.[1];
    const pathname = auth0State.targetUrl.match(/[^\(]*/)?.[0] || '/';

    !!queryParams
      ? window.location.replace(auth0State.href)
      : setTimeout(
          () =>
            this.router.navigate([pathname]).then(() => {
              this.handleRouterOutlets(outlets);
            }),
          50,
        );
  }

  private handleRouterOutlets(outlets: string) {
    if (outlets) {
      const outletsObject = {};
      const key = outlets.match(/[^:]*/)?.[0];
      outletsObject[key] = outlets.match(/\:(.*)/)?.[1];
      this.router.navigate([{ outlets: outletsObject }]);
    }
  }
}
